import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaTimes,
  FaHeart,
  FaHeartBroken,
  FaComment,
  FaUserPlus,
  FaBell,
  FaShoppingCart,
  FaCheckDouble,
  FaEnvelope,
  FaStar,
  FaCoins,
} from 'react-icons/fa';
import { useAppDispatch, useAppSelector } from '../../redux/Store';
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  dismissNotification,
  fetchUnreadNotificationCount,
} from '../../redux/thunks/notificationsThunks';
import defaultAvatar from '../../images/default_avatar.png';
import { getMediaUrl } from '@/constants/media';
import { formatRelativeTime } from '@/utils/formatTime';
import './NotificationsPanel.scss';

interface Notification {
  id: string;
  type: 'like' | 'dislike' | 'comment' | 'follow' | 'mention' | 'system' | 'profile_purchase' | 'profile_sale' | 'message' | 'spotlight_received' | 'spotlight_sent' | 'royalty_earned' | 'achievement_earned';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  avatar?: string;
  link?: string;
}

type NotificationTab = 'all' | 'mentions' | 'likes' | 'follows' | 'purchases' | 'messages' | 'spotlights' | 'royalties' | 'system';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<NotificationTab>('all');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const reduxNotifications = useAppSelector((state) => state.notifications.notifications);
  const isLoaded = useAppSelector((state) => state.notifications.isLoaded);

  // Map Redux notifications to component Notification type
  const notifications: Notification[] = reduxNotifications.map(n => ({
    id: n.id,
    type: n.type,
    title: n.title,
    message: n.message,
    timestamp: new Date(n.timestamp),
    read: n.read,
    avatar: n.avatar,
    link: n.link,
  }));

  // Fetch notifications when panel opens (only if not already loaded)
  useEffect(() => {
    if (isOpen && !isLoaded) {
      setIsLoading(true);
      dispatch(fetchNotifications({})).finally(() => setIsLoading(false));
    }
  }, [isOpen, isLoaded, dispatch]);

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'like':
        return <FaHeart className="notification-icon like" />;
      case 'dislike':
        return <FaHeartBroken className="notification-icon dislike" />;
      case 'comment':
      case 'mention':
        return <FaComment className="notification-icon comment" />;
      case 'follow':
        return <FaUserPlus className="notification-icon follow" />;
      case 'profile_purchase':
      case 'profile_sale':
        return <FaShoppingCart className="notification-icon purchase" />;
      case 'message':
        return <FaEnvelope className="notification-icon message" />;
      case 'spotlight_received':
      case 'spotlight_sent':
        return <FaStar className="notification-icon spotlight" />;
      case 'royalty_earned':
        return <FaCoins className="notification-icon royalty" />;
      case 'system':
        return <FaBell className="notification-icon system" />;
      default:
        return <FaBell className="notification-icon" />;
    }
  };

  const filterNotifications = (): Notification[] => {
    switch (activeTab) {
      case 'mentions':
        return notifications.filter((n) => n.type === 'mention');
      case 'likes':
        return notifications.filter((n) => n.type === 'like' || n.type === 'dislike');
      case 'follows':
        return notifications.filter((n) => n.type === 'follow');
      case 'purchases':
        return notifications.filter((n) => n.type === 'profile_purchase' || n.type === 'profile_sale');
      case 'messages':
        return notifications.filter((n) => n.type === 'message');
      case 'spotlights':
        return notifications.filter((n) => n.type === 'spotlight_received' || n.type === 'spotlight_sent');
      case 'royalties':
        return notifications.filter((n) => n.type === 'royalty_earned');
      case 'system':
        return notifications.filter((n) => n.type === 'system');
      default:
        return notifications;
    }
  };


  const handleMarkAllRead = async () => {
    try {
      await dispatch(markAllNotificationsAsRead()).unwrap();
      // Re-sync the badge from the backend in case the local reducer drifted
      // (e.g. a socket-pushed notification arrived during the request).
      dispatch(fetchUnreadNotificationCount());
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDismiss = async (id: string) => {
    try {
      await dispatch(dismissNotification(id)).unwrap();
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      try {
        await dispatch(markNotificationAsRead(notification.id)).unwrap();
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }

    // Navigate if link exists
    if (notification.link) {
      // Extract pathname from full URL if needed (backend sends full URLs like http://localhost:3000/post/123)
      // React Router expects relative paths like /post/123
      let path = notification.link;
      try {
        const url = new URL(notification.link);
        path = url.pathname; // Extract just /post/123 from http://localhost:3000/post/123
      } catch {
        // If it's not a full URL, use as-is (already a relative path)
        path = notification.link;
      }

      navigate(path);
      onClose();
    }
  };

  const filteredNotifications = filterNotifications();
  const unreadCount = useAppSelector((state) => state.notifications.unreadCount);

  // Track which notifications the user has expanded to read in full
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const EXPAND_THRESHOLD = 120;
  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="notifications-overlay" onClick={onClose} />}

      {/* Side Panel */}
      <div className={`notifications-panel ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="notifications-header">
          <div>
            <h2 className="notifications-title">Notifications</h2>
            {unreadCount > 0 && (
              <span className="notifications-unread-count">{unreadCount} unread</span>
            )}
          </div>
          <div className="notifications-header-actions">
            <button
              className="notifications-mark-read-btn"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
              aria-label="Mark all as read"
              title="Mark all as read"
            >
              <FaCheckDouble />
              <span className="mark-read-label">Mark all read</span>
            </button>
            <button
              className="notifications-close-btn"
              onClick={onClose}
              aria-label="Close notifications"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="notifications-tabs">
          {(['all', 'mentions', 'likes', 'follows', 'purchases', 'messages', 'spotlights', 'royalties', 'system'] as NotificationTab[]).map((tab) => (
            <button
              key={tab}
              className={`notifications-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Notifications List */}
        <div className="notifications-list">
          {isLoading ? (
            <div className="notifications-empty">
              <div className="notifications-spinner" />
              <p>Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="notifications-empty">
              <FaBell />
              <p>No notifications</p>
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="notification-icon-wrapper">
                  {['like', 'dislike', 'comment', 'follow', 'mention', 'profile_purchase', 'profile_sale', 'message', 'spotlight_received', 'spotlight_sent', 'royalty_earned'].includes(notification.type) ? (
                    <img src={getMediaUrl(notification.avatar || null) || defaultAvatar} alt="" className="notification-avatar" />
                  ) : notification.avatar ? (
                    <img src={getMediaUrl(notification.avatar) || undefined} alt="" className="notification-avatar" />
                  ) : (
                    getNotificationIcon(notification.type)
                  )}
                </div>

                <div className="notification-content">
                  <p className="notification-title">{notification.title}</p>
                  <p
                    className={`notification-message ${expandedIds.has(notification.id) ? 'expanded' : ''}`}
                  >
                    {notification.message}
                  </p>
                  {notification.message.length > EXPAND_THRESHOLD && (
                    <button
                      type="button"
                      className="notification-expand-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(notification.id);
                      }}
                    >
                      {expandedIds.has(notification.id) ? 'Show less' : 'Show more'}
                    </button>
                  )}
                  <span className="notification-timestamp">
                    {formatRelativeTime(notification.timestamp)}
                  </span>
                </div>

                <button
                  className="notification-dismiss-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDismiss(notification.id);
                  }}
                  aria-label="Dismiss"
                >
                  <FaTimes />
                </button>

                {!notification.read && <div className="notification-unread-indicator" />}
              </div>
            ))
          )}
        </div>

      </div>
    </>
  );
};

export default NotificationsPanel;
